"use client";

import { useShallowSearchParam, setShallowSearchParams } from "@/lib/hooks/use-shallow-search-param";
import { SPORT_URL_KEY, type SportBadge } from "@/lib/conditions/unified-types";
import { sportLabel, sportIcon, sportIsBeta } from "@/lib/conditions/sport-meta";
import { resolveSlotSport } from "@/lib/conditions/sport-aware-slot";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { isWithheldSkiLabelFor } from "@/lib/companion/ski-withheld";

/**
 * The text an option shows beside its sport (forecast areas spec 10.4 item 6): the score out of
 * 100 ("Surf 72"), the ski v3 label when a ski score is withheld ("Piste · Closed"), and nothing
 * for any other missing score, so an unscored sport never shows an invented number.
 */
export function sportOptionLabel(badge: Pick<SportBadge, "sport" | "score" | "label" | "offSeason" | "forecast">): string {
  const base = `${sportLabel(badge.sport)}${sportIsBeta(badge.sport) ? " (Beta)" : ""}`;
  const score = badge.score !== null && Number.isFinite(badge.score) ? ` ${scoreOutOf100(badge.score)}` : "";
  const withheld = badge.score === null && badge.label !== null && isWithheldSkiLabelFor(badge.sport, badge.label)
    ? ` · ${badge.label}`
    : "";
  // The nightly gap: the previous run's forecast for today, never today's score.
  const forecast = badge.forecast && (score || withheld) ? " (forecast)" : "";
  return `${base}${score}${withheld}${forecast}${badge.offSeason ? " · Off season" : ""}`;
}

/** A stable picker always names the selected sport, including lower-ranked sports. */
export function SportSelector({ badges, defaultSport }: { badges: SportBadge[]; defaultSport: string }) {
  const rawSport = useShallowSearchParam("sport");
  if (badges.length === 0) return null;
  const activeSport = resolveSlotSport(rawSport, defaultSport, badges.map(({ sport }) => sport));
  return (
    <label className="flex min-h-11 items-center gap-3 text-[12px] font-semibold text-text-muted">
      <span className="shrink-0">Sport</span>
      <span aria-hidden="true" className="text-[18px]">{sportIcon(activeSport)}</span>
      <select
        aria-label="Choose sport"
        value={activeSport}
        onChange={(event) => setShallowSearchParams({ sport: SPORT_URL_KEY[event.target.value] ?? event.target.value })}
        className="min-h-11 min-w-0 flex-1 rounded-[10px] border border-border bg-white px-3 pr-8 text-[14px] font-bold text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal md:max-w-[320px]"
      >
        {badges.map((badge) => (
          <option key={badge.sport} value={badge.sport}>
            {sportOptionLabel(badge)}
          </option>
        ))}
      </select>
      <span className="ml-auto hidden text-[12px] font-normal md:block">{badges.some((badge) => badge.forecast) ? "Forecast scores out of 100 for today" : "Scores out of 100 for today"}</span>
    </label>
  );
}
